/**
 * ProductStockService
 * Service for ProductStock entity operations
 * Route: api/product-stock
 * Controller: ProductStockController.cs
 */
import { BaseService } from '../base/BaseService';
/**
 * ProductStockService
 * Handles stock management operations with composite keys
 */
export class ProductStockService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? ProductStockService.DEFAULT_SERVICE_PATH,
            basePath: ProductStockService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== LOOKUPS ====================
    /**
     * Get stock by composite key
     * GET /api/product-stock?productId=&sizeId=&colorId=&locationId=
     */
    async getStock(productId, sizeId, colorId, locationId) {
        const response = await this.getStockRaw(productId, sizeId, colorId, locationId);
        if (!response.data) {
            throw new Error('Stock record not found');
        }
        return response.data;
    }
    /**
     * Get stock by composite key (raw)
     */
    getStockRaw(productId, sizeId, colorId, locationId) {
        const url = this.buildUrlWithParams(undefined, { productId, sizeId, colorId, locationId });
        return this.http.get(url);
    }
    /**
     * Get all stock records with optional location filter
     * GET /api/product-stock/all?locationId=
     */
    async getAll(locationId) {
        const response = await this.getAllRaw(locationId);
        return response.data ?? [];
    }
    /**
     * Get all stock records with optional location filter (raw)
     */
    getAllRaw(locationId) {
        const endpoint = this.getEndpoint('all', 'all');
        const url = this.buildUrlWithParams(endpoint, { locationId });
        return this.http.get(url);
    }
    /**
     * Get low stock items for a location
     * GET /api/product-stock/low-stock?locationId=
     */
    async getLowStock(locationId) {
        const response = await this.getLowStockRaw(locationId);
        return response.data ?? [];
    }
    /**
     * Get low stock items for a location (raw)
     */
    getLowStockRaw(locationId) {
        const endpoint = this.getEndpoint('low-stock', 'low-stock');
        const url = this.buildUrlWithParams(endpoint, { locationId });
        return this.http.get(url);
    }
    // ==================== MUTATIONS ====================
    /**
     * Create a stock record
     * POST /api/product-stock
     */
    async createStock(stock) {
        const response = await this.createStockRaw(stock);
        if (!response.data) {
            throw new Error('Failed to create stock record');
        }
        return response.data;
    }
    /**
     * Create a stock record (raw)
     */
    createStockRaw(stock) {
        const url = this.buildUrl();
        return this.http.post(url, stock);
    }
    /**
     * Adjust stock atomically (creates transaction)
     * POST /api/product-stock/adjust
     */
    async adjustStock(request) {
        const response = await this.adjustStockRaw(request);
        if (!response.data) {
            throw new Error('Failed to adjust stock');
        }
        return response.data;
    }
    /**
     * Adjust stock atomically (raw)
     */
    adjustStockRaw(request) {
        const endpoint = this.getEndpoint('adjust', 'adjust');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, request);
    }
    /**
     * Update a stock record by composite key
     * PUT /api/product-stock?productId=&sizeId=&colorId=&locationId=
     */
    async updateStock(productId, sizeId, colorId, locationId, stock) {
        const response = await this.updateStockRaw(productId, sizeId, colorId, locationId, stock);
        if (!response.data) {
            throw new Error('Failed to update stock record');
        }
        return response.data;
    }
    /**
     * Update a stock record by composite key (raw)
     */
    updateStockRaw(productId, sizeId, colorId, locationId, stock) {
        const url = this.buildUrlWithParams(undefined, { productId, sizeId, colorId, locationId });
        return this.http.put(url, stock);
    }
    /**
     * Delete a stock record by composite key
     * DELETE /api/product-stock?productId=&sizeId=&colorId=&locationId=
     */
    async deleteStock(productId, sizeId, colorId, locationId) {
        const response = await this.deleteStockRaw(productId, sizeId, colorId, locationId);
        return response.data ?? response.success;
    }
    /**
     * Delete a stock record by composite key (raw)
     */
    deleteStockRaw(productId, sizeId, colorId, locationId) {
        const url = this.buildUrlWithParams(undefined, { productId, sizeId, colorId, locationId });
        return this.http.delete(url);
    }
}
ProductStockService.DEFAULT_SERVICE_PATH = 'products';
ProductStockService.BASE_PATH = 'api/product-stock';
//# sourceMappingURL=ProductStockService.js.map